import React, { useRef, useState } from "react";
import { Eye, Loader2, Sparkles, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import GameCanvas from "@/components/GameCanvas";

const scoreColor = (n) => (n >= 85 ? "#10b981" : n >= 60 ? "#f59e0b" : "#f43f5e");
const SEV = {
  high: "border-rose-500/40 text-rose-400 bg-rose-500/5",
  medium: "border-amber-500/40 text-amber-400 bg-amber-500/5",
  low: "border-sky-500/40 text-sky-400 bg-sky-500/5",
};
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// AI Visual QA — samples live gameplay frames from the playable build and runs a GPT-5 vision model
// to flag floating objects, missing textures and broken lighting. High/medium defects are merged into
// the QA report so Auto-Polish can fix them.
export default function VisualQAScan({ project, projectId, reload, disabled }) {
  const canvasRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [previewOn, setPreviewOn] = useState(false);
  const result = project.visual_qa;
  const gameId = project.game_id;

  const ensureConfig = async () => {
    if (config) return config;
    const { data } = await api.get(`/games/${gameId}`);
    const cfg = data.config || data.game?.config || null;
    setConfig(cfg);
    return cfg;
  };

  const scan = async () => {
    if (scanning || !gameId) return;
    setScanning(true);
    try {
      const cfg = await ensureConfig();
      if (!cfg) { toast.error("Couldn't load the game to scan."); setScanning(false); return; }
      setPreviewOn(true);
      await wait(2800); // let the engine render the world
      const frames = [];
      for (let i = 0; i < 3; i++) {
        const shot = canvasRef.current ? await canvasRef.current.capture() : null;
        if (shot) frames.push(shot);
        await wait(1300);
      }
      if (!frames.length) { toast.error("Couldn't grab any frames — let the preview load, then retry."); setScanning(false); return; }
      const { data } = await api.post(`/gv2/projects/${projectId}/visual-qa`, { frames }, { timeout: 120000 });
      const n = data.visual_qa?.defects?.length || 0;
      toast.success(`Visual QA done — score ${data.visual_qa.score} · ${n} defect${n === 1 ? "" : "s"}.`);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Visual QA failed — try again.");
    } finally {
      setScanning(false);
    }
  };

  if (!gameId) return null;

  return (
    <div className="rounded-2xl border border-fuchsia-500/20 bg-fuchsia-500/[0.03] p-4 mb-4" data-testid="gv2-visual-qa">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-sm font-bold"><Eye className="w-4 h-4 text-fuchsia-400" /> AI Visual QA · GPT-5 vision</div>
        <Button size="sm" variant="outline" disabled={disabled || scanning} onClick={scan}
          data-testid="gv2-visual-qa-run" className="rounded-full h-7 px-3 text-xs gap-1.5">
          {scanning ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scanning…</> : <><Sparkles className="w-3.5 h-3.5" /> {result ? "Re-scan visuals" : "Scan visuals"} <span className="opacity-70">· 3 cr</span></>}
        </Button>
      </div>
      <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug mb-2">
        A vision model samples live gameplay frames and flags <b>floating objects, missing textures and broken lighting</b>. High &amp; medium defects are added to the QA report so Auto-Polish can fix them.
      </p>

      {previewOn && (
        <div className="mb-3" data-testid="gv2-visual-qa-preview">
          <GameCanvas ref={canvasRef} config={config} compact previewMode />
          {scanning && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Capturing frames for inspection…</div>}
        </div>
      )}

      {result && (
        <div className="space-y-2.5" data-testid="gv2-visual-qa-report">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-extrabold tabular-nums" style={{ color: scoreColor(result.score) }} data-testid="gv2-visual-qa-score">{result.score}</div>
            <div className="min-w-0">
              <p className="text-[12px] leading-snug">{result.verdict}</p>
              <p className="text-[10px] text-[hsl(var(--muted-foreground))]">{result.frames_analyzed} frame(s) analysed{result.at ? ` · ${new Date(result.at).toLocaleString()}` : ""}</p>
            </div>
          </div>
          {(result.defects || []).length === 0 ? (
            <div className="flex items-center gap-2 text-[12px] text-emerald-500 font-medium" data-testid="gv2-visual-qa-clean"><ShieldCheck className="w-4 h-4" /> No visual defects detected — renders clean! 🌟</div>
          ) : (
            <div className="space-y-1.5">
              {result.defects.map((d, i) => (
                <div key={i} className={`rounded-xl border p-2.5 ${SEV[d.severity] || SEV.medium}`} data-testid={`gv2-visual-defect-${i}`}>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Badge variant="outline" className="text-[9px] uppercase">{d.severity}</Badge>
                    <Badge variant="outline" className="text-[9px] capitalize">{d.type}</Badge>
                    {d.area && <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{d.area}</span>}
                  </div>
                  <div className="text-[12px] font-medium text-[hsl(var(--foreground))]">{d.finding}</div>
                  {d.fix && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">🔧 {d.fix}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
